import React, { FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaEnvelope,
  FaPlus,
  FaSearch,
  FaTimes,
  FaUsers
} from "react-icons/fa";
import { userRepository } from "src/features/users/repositories/user.repository";
import { User } from "src/features/users/types/user.entity";
import { showToast } from "src/utils/toast.util";
import {
  useSendAdminBroadcastEmailMutation,
  AdminBroadcastContent
} from "../admin-email.service";
import {
  MAX_ADMIN_EMAIL_RECIPIENTS,
  parseRecipientIds
} from "../utils/recipient-ids.util";

type RecipientDetails = Pick<User, "id" | "name" | "email">;

const initialContent: AdminBroadcastContent = {
  subject: "",
  preheader: "",
  label: "",
  title: "",
  bodyHtml: "",
  ctaText: "",
  ctaUrl: "",
  language: "en",
  direction: "ltr"
};

const AdminEmailPage = () => {
  const [content, setContent] = useState<AdminBroadcastContent>(initialContent);
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [recipientDetails, setRecipientDetails] = useState<Record<string, RecipientDetails>>({});
  const [lookupValue, setLookupValue] = useState("");
  const [pastedIds, setPastedIds] = useState("");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [sendAdminBroadcastEmail, { isLoading: isSending }] = useSendAdminBroadcastEmailMutation();

  const recipientsById = useMemo(
    () => new Map(Object.entries(recipientDetails)),
    [recipientDetails]
  );

  const addRecipientIds = (ids: string[]): number | null => {
    const mergedIds = Array.from(new Set([...recipientIds, ...ids]));
    if (mergedIds.length > MAX_ADMIN_EMAIL_RECIPIENTS) {
      showToast(
        "error",
        `A Customer.io broadcast can contain at most ${MAX_ADMIN_EMAIL_RECIPIENTS.toLocaleString()} recipients.`
      );
      return null;
    }
    setRecipientIds(mergedIds);
    return mergedIds.length - recipientIds.length;
  };

  const addUser = (user: RecipientDetails) => {
    const added = addRecipientIds([user.id]);
    if (added === null) return;
    setRecipientDetails((current) => ({ ...current, [user.id]: user }));
  };

  const lookupUser = async () => {
    const identifier = lookupValue.trim();
    if (!identifier) return;

    setIsLookingUp(true);
    try {
      const user = await userRepository.getByUnique(identifier);
      if (!user) {
        showToast("error", "No user matches that exact ID, email address, or phone number.");
        return;
      }

      addUser(user);
      setLookupValue("");
      showToast("success", `${user.name || user.id} added to recipients.`);
    } catch (error) {
      showToast("error", error instanceof Error ? error.message : "Could not look up the user.");
    } finally {
      setIsLookingUp(false);
    }
  };

  const addPastedIds = () => {
    const ids = parseRecipientIds(pastedIds);
    if (!ids.length) {
      showToast("error", "Paste at least one user ID.");
      return;
    }

    const added = addRecipientIds(ids);
    if (added === null) return;
    setPastedIds("");
    showToast("success", `${added.toLocaleString()} new IDs added.`);
  };

  const removeRecipient = (userId: string) => {
    setRecipientIds((current) => current.filter((id) => id !== userId));
  };

  const updateContent = <K extends keyof AdminBroadcastContent>(
    key: K,
    value: AdminBroadcastContent[K]
  ) => setContent((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!recipientIds.length) {
      showToast("error", "Add at least one recipient before sending.");
      return;
    }
    if (!content.subject.trim() || !content.title.trim() || !content.bodyHtml.trim()) {
      showToast("error", "Subject, title, and message body are required.");
      return;
    }
    if (Boolean(content.ctaText?.trim()) !== Boolean(content.ctaUrl?.trim())) {
      showToast("error", "Button text and button URL must be provided together.");
      return;
    }

    const confirmed = window.confirm(
      `Send this email to ${recipientIds.length.toLocaleString()} selected recipient${recipientIds.length === 1 ? "" : "s"}?`
    );
    if (!confirmed) return;

    try {
      const result = await sendAdminBroadcastEmail({
        userIds: recipientIds,
        content
      }).unwrap();

      showToast(
        "success",
        `Broadcast queued for ${result.requestedRecipientCount.toLocaleString()} recipients (trigger ${result.triggerId}).`
      );
      setRecipientIds([]);
      setRecipientDetails({});
      setContent(initialContent);
    } catch (error) {
      showToast("error", (error as { data?: string })?.data || "Could not queue the email broadcast.");
    }
  };

  return (
    <div className="mx-auto min-h-screen max-w-6xl space-y-6 px-4 py-8 text-slate-900">
      <header className="rounded-3xl bg-gradient-to-r from-indigo-700 via-violet-700 to-purple-700 px-6 py-7 text-white shadow-xl">
        <div className="flex items-start gap-4">
          <span className="rounded-2xl bg-white/15 p-3">
            <FaEnvelope className="h-6 w-6" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-indigo-100">Customer.io broadcast</p>
            <h1 className="mt-1 text-3xl font-bold">Send admin email</h1>
            <p className="mt-2 max-w-3xl text-sm text-indigo-100">
              Choose registered users or paste their Firebase user IDs. Customer.io will skip IDs that do not exist in its audience.
            </p>
          </div>
        </div>
      </header>

      <form onSubmit={submit} className="space-y-6">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">Recipients</h2>
              <p className="mt-1 text-sm text-slate-600">
                {recipientIds.length.toLocaleString()} selected of {MAX_ADMIN_EMAIL_RECIPIENTS.toLocaleString()} maximum.
              </p>
            </div>
            <Link to="/users" className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900">
              <FaUsers /> Open user directory
            </Link>
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="font-semibold">Find and add one user</h3>
              <p className="mt-1 text-sm text-slate-600">Use an exact Firebase user ID, email address, or phone number.</p>
              <div className="mt-4 flex gap-2">
                <input
                  value={lookupValue}
                  onChange={(event) => setLookupValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    void lookupUser();
                  }}
                  placeholder="User ID or email"
                  className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2"
                />
                <button
                  type="button"
                  onClick={() => void lookupUser()}
                  disabled={isLookingUp}
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaSearch /> {isLookingUp ? "Looking up" : "Add"}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="font-semibold">Paste a list of user IDs</h3>
              <p className="mt-1 text-sm text-slate-600">Separate IDs with a new line, comma, semicolon, or space.</p>
              <textarea
                value={pastedIds}
                onChange={(event) => setPastedIds(event.target.value)}
                placeholder={"uid-one\nuid-two\nuid-three"}
                className="mt-4 min-h-[108px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-mono text-xs outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2"
              />
              <button
                type="button"
                onClick={addPastedIds}
                className="mt-3 inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
              >
                <FaPlus /> Add pasted IDs
              </button>
            </div>
          </div>

          {recipientIds.length > 0 ? (
            <ul className="mt-5 grid max-h-72 gap-2 overflow-y-auto sm:grid-cols-2">
              {recipientIds.map((userId) => {
                const user = recipientsById.get(userId);
                return (
                  <li key={userId} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{user?.name || "Pasted user ID"}</p>
                      <p className="truncate font-mono text-xs text-slate-500">{user?.email || userId}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRecipient(userId)}
                      className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      aria-label={`Remove ${userId}`}
                    >
                      <FaTimes />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold">Email content</h2>
          <p className="mt-1 text-sm text-slate-600">Use simple HTML in the message body, for example paragraphs, links, lists, and bold text.</p>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold">Subject
              <input required value={content.subject} onChange={(event) => updateContent("subject", event.target.value)} maxLength={200} className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
            </label>
            <label className="text-sm font-semibold">Preheader <span className="font-normal text-slate-500">(optional)</span>
              <input value={content.preheader} onChange={(event) => updateContent("preheader", event.target.value)} maxLength={200} className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
            </label>
            <label className="text-sm font-semibold">Label <span className="font-normal text-slate-500">(optional)</span>
              <input value={content.label} onChange={(event) => updateContent("label", event.target.value)} maxLength={80} className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
            </label>
            <label className="text-sm font-semibold">Title
              <input required value={content.title} onChange={(event) => updateContent("title", event.target.value)} maxLength={300} className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
            </label>
          </div>

          <label className="mt-4 block text-sm font-semibold">Message body (HTML)
            <textarea required value={content.bodyHtml} onChange={(event) => updateContent("bodyHtml", event.target.value)} maxLength={40_000} className="mt-1.5 min-h-[240px] w-full rounded-xl border border-slate-300 px-3 py-2.5 font-mono text-sm font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
          </label>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold">Button text <span className="font-normal text-slate-500">(optional)</span>
              <input value={content.ctaText} onChange={(event) => updateContent("ctaText", event.target.value)} maxLength={80} className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
            </label>
            <label className="text-sm font-semibold">Button URL <span className="font-normal text-slate-500">(optional)</span>
              <input type="url" value={content.ctaUrl} onChange={(event) => updateContent("ctaUrl", event.target.value)} maxLength={2_000} placeholder="https://taaly.nl" className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
            </label>
            <label className="text-sm font-semibold">Language
              <input value={content.language} onChange={(event) => updateContent("language", event.target.value)} maxLength={35} placeholder="en" className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2" />
            </label>
            <label className="text-sm font-semibold">Text direction
              <select value={content.direction} onChange={(event) => updateContent("direction", event.target.value as "ltr" | "rtl")} className="mt-1.5 block w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal outline-none ring-indigo-200 focus:border-indigo-500 focus:ring-2">
                <option value="ltr">Left to right</option>
                <option value="rtl">Right to left</option>
              </select>
            </label>
          </div>
        </section>

        <div className="flex justify-end">
          <button type="submit" disabled={isSending || !recipientIds.length} className="inline-flex items-center gap-2 rounded-xl bg-indigo-700 px-6 py-3 font-semibold text-white shadow-sm hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50">
            <FaEnvelope /> {isSending ? "Queueing broadcast..." : `Send to ${recipientIds.length.toLocaleString()} recipients`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminEmailPage;
